import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import VisualizerBackground from '../components/VisualizerBackground';
import { useToast } from '../hooks/use-toast';
import { ArrowDown, ArrowUp } from 'lucide-react';
import ScrollAnimation from '../components/ScrollAnimation';
import ParallaxSection from '../components/ParallaxSection';

// Helper function to parse query parameters
function useQuery() {
  return new URLSearchParams(useSearchParams().toString());
}

const Contact = () => {
  const { toast } = useToast();
  const query = useQuery();
  const serviceParam = query.get('service');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: serviceParam || '',
    message: '',
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  
  // Update service when query parameter changes
  useEffect(() => {
    if (serviceParam) {
      setFormData(prev => ({
        ...prev,
        service: serviceParam
      }));
      
      // Add pre-filled message for sponsorship inquiries
      if (serviceParam === 'sponsorship') {
        setFormData(prev => ({
          ...prev,
          message: "I'm interested in sponsorship opportunities. Here's a bit about my brand/company and what I'm looking for in a partnership:"
        }));
      }
    }
  }, [serviceParam]);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // With Netlify Forms, the form will be handled automatically
    // Just need to let the form submit naturally with a hidden field
    const form = e.target as HTMLFormElement;
    
    // Simulate a delay to show the loading state
    setTimeout(() => {
      form.submit();
      
      // The page will reload on submission, but we'll show the loading state briefly
      toast({
        title: "Sending message...",
        description: "Please wait while your message is being sent.",
      });
    }, 500);
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  
  // FAQ data
  const faqs = [
    {
      question: "What services do you offer?",
      answer: "I offer a range of services including session drumming, music production, content creation, and drum repair & tuning. Each service is tailored to meet your specific needs, whether you're looking for professional drum performances, complete music production, video content, or drum maintenance services."
    },
    {
      question: "How much do your services cost?",
      answer: "Pricing varies depending on the service and project scope. Session drumming rates are typically hourly or per project. Music production and content creation are quoted based on complexity. Drum repair and tuning services have standard rates for different types of work. Contact me for a detailed quote specific to your needs."
    },
    {
      question: "What's included in your drum repair and tuning service?",
      answer: "My drum repair and tuning service includes complete drum maintenance, head replacement, bearing edge work, hardware repair, and professional tuning. I can work on all types of drums and percussion instruments, ensuring they sound and perform at their best."
    },
    {
      question: "How long does a typical session or project take?",
      answer: "Session drumming typically takes 2-4 hours per song. Music production projects vary from a few days to several weeks depending on complexity. Content creation timelines depend on the scope of the project. Drum repair and tuning usually takes 1-2 hours per drum, depending on the work needed."
    },
    {
      question: "Do you provide remote services?",
      answer: "Yes, I offer remote services for music production and content creation. Session drumming and drum repair require in-person work. I'm available to travel for projects, with travel expenses included in the project quote."
    },
    {
      question: "What equipment do you use?",
      answer: "I use professional-grade equipment for all services. For drumming and music production, I have a collection of high-end acoustic and electronic drums, microphones, and recording equipment. For content creation, I use professional cameras, lighting, and audio equipment. For drum repair, I have specialized tools and equipment for all types of drum maintenance."
    }
  ];
  
  return (
    <div className="min-h-screen bg-drummer-dark text-white">
      <VisualizerBackground />
      
      {/* Hero */}
      <section className="pt-24 pb-8 md:pt-32 md:pb-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-client-yellow via-client-yellow to-client-teal animate-gradient-x">Get in Touch</span>
            </h1>
            <p className="text-xl text-white">
              Ready to collaborate? Reach out and let's create something amazing together.
            </p>
          </div>
        </div>
      </section>
      
      {/* Contact Form Section */}
      <section className="py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Contact Information</h2>
                <div className="space-y-6">
                  <div className="flex items-start">
                    <div className="mt-1 bg-drummer-purple/20 p-3 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-drummer-purple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Email</h3>
                      <a href="mailto:contact@daryiankelton.com" className="text-drummer-neon hover:underline">
                        contact@daryiankelton.com
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="mt-1 bg-drummer-blue/20 p-3 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-drummer-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Phone</h3>
                      <p className="text-white">Available upon request</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="mt-1 bg-drummer-pink/20 p-3 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-drummer-pink" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                      </svg>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold">Social Media</h3>
                      <div className="flex space-x-3 mt-2">
                        <a href="https://instagram.com/drummerboy_x" target="_blank" rel="noopener noreferrer" className="text-white hover:text-client-yellow transition-colors" aria-label="Follow Daryian Kelton on Instagram">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                          <span className="sr-only">Instagram</span>
                        </a>
                        <a href="https://youtube.com/@DaryianKelton" target="_blank" rel="noopener noreferrer" className="text-white hover:text-client-yellow transition-colors" aria-label="Subscribe to Daryian Kelton on YouTube">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"></path><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"></polygon></svg>
                          <span className="sr-only">YouTube</span>
                        </a>
                        <a href="https://twitter.com/drummerboy_x" target="_blank" rel="noopener noreferrer" className="text-white hover:text-client-yellow transition-colors" aria-label="Follow Daryian Kelton on Twitter">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
                          <span className="sr-only">Twitter</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-12">
                  <h2 className="text-2xl font-bold mb-6">Office Hours</h2>
                  <p className="text-white mb-4">
                    Available for meetings and consultations:
                  </p>
                  <ul className="space-y-2 text-white">
                    <li>Monday - Friday: 10AM - 6PM</li>
                    <li>Saturday: By appointment only</li>
                    <li>Sunday: Closed</li>
                  </ul>
                </div>
              </div>
              
              {/* Contact Form */}
              <div>
                <h2 className="text-2xl font-bold mb-6">Send a Message</h2>
                <form 
                  onSubmit={handleSubmit} 
                  className="space-y-6"
                  name="contact"
                  method="POST"
                  data-netlify="true"
                  netlify-honeypot="bot-field"
                  action="/success?form=contact"
                >
                  <input type="hidden" name="form-name" value="contact" />
                  <div className="hidden">
                    <label>
                      Don't fill this out if you're human: <input name="bot-field" />
                    </label>
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-1">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-white mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                    />
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="service" className="block text-white text-sm font-medium mb-2">
                      Service of Interest
                    </label>
                    <select
                      id="service"
                      name="service"
                      className="w-full px-4 py-3 bg-client-black/30 border border-client-gray/20 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-client-yellow focus:border-client-yellow"
                      value={formData.service}
                      onChange={handleChange}
                    >
                      <option value="">Select a service</option>
                      <option value="session-drummer">Session Drummer</option>
                      <option value="music-production">Music Production</option>
                      <option value="content-creation">Content Creation</option>
                      <option value="drum-repair">Drum Repair & Tuning</option>
                      <option value="sponsorship">Become a Sponsor</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-white mb-1">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 rounded-md bg-client-black/30 border border-client-gray/20 focus:border-client-yellow focus:ring-1 focus:ring-client-yellow focus:outline-none text-white"
                    ></textarea>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="drum-button w-full flex justify-center items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-8 md:py-12 bg-black/30">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold mb-8 text-center" id="faqs">Frequently Asked Questions</h2>
          
          <div className="max-w-3xl mx-auto">
            <div className="space-y-4">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index;
                return (
                  <div 
                    key={index}
                    className="glassmorphic overflow-hidden transition-all duration-300"
                  >
                    {isOpen ? (
                      <button
                        className="w-full flex justify-between items-center py-4 px-6 focus:outline-none focus:ring-2 focus:ring-client-yellow rounded-lg bg-drummer-dark/50"
                        onClick={() => toggleFaq(index)}
                        aria-expanded="true"
                        aria-controls={`faq-content-${index}`}
                      >
                        <span className="text-lg font-medium">{faq.question}</span>
                        <ArrowUp className="h-5 w-5 text-drummer-neon" aria-hidden="true" />
                      </button>
                    ) : (
                      <button
                        className="w-full flex justify-between items-center py-4 px-6 focus:outline-none focus:ring-2 focus:ring-client-yellow rounded-lg bg-drummer-dark/50"
                        onClick={() => toggleFaq(index)}
                        aria-expanded="false"
                        aria-controls={`faq-content-${index}`}
                      >
                        <span className="text-lg font-medium">{faq.question}</span>
                        <ArrowDown className="h-5 w-5 text-drummer-neon" aria-hidden="true" />
                      </button>
                    )}
                    
                    <div 
                      id={`faq-content-${index}`}
                      className={`px-6 transition-all duration-300 overflow-hidden ${
                        isOpen ? 'max-h-96 pb-6' : 'max-h-0'
                      }`}
                    >
                      <p className="text-white">{faq.answer}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      
      {/* Call to Action */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4">
          <div className="glassmorphic p-6 md:p-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Create Together?</h2>
              <p className="text-xl text-white mb-6">
                Explore my services, check out my work, or reach out to start a conversation about your project.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/about"
                  className="drum-button"
                >
                  Learn About Daryian
                </Link>
                <Link 
                  to="/services" 
                  className="drum-button-outline"
                >
                  View Services
                </Link>
                <Link 
                  to="/videos" 
                  className="drum-button-secondary"
                >
                  Watch Videos
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
